import React, { Fragment, useEffect, useState, useMemo } from "react";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import API from "../../../api/baseApi";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import moment from "moment";
import Swal from "sweetalert2";

const FailedCreditServices = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const client = useSelector((state) => state.clients.selectedClient?.value);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const res = await API.get("/customers/failedCreditServices");
      if (res.data?.success) {
        setData(res.data.data || []);
      } else {
        toast.error("Failed to fetch failed credit services");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching failed credit services");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = async (customerId, provider) => {
    try {
      setIsLoading(true);
      const res = await API.post("/customers/retryFailedCreditService", { customer_id: customerId, provider });
      
      const responseData = res.data?.rawData ? JSON.stringify(res.data.rawData, null, 2) : "No raw data available";
      const requestPayload = res.data?.payload ? JSON.stringify(res.data.payload, null, 2) : "No payload available";
      const message = res.data?.message || "Processed Request";

      const alertHtml = `
        <div style="text-align: left;">
          <h6 style="margin-bottom: 5px; font-weight: bold; font-size: 14px;">Request Payload:</h6>
          <pre style="font-size: 11px; background: #f4f4f4; padding: 10px; border-radius: 5px; max-height: 200px; overflow-y: auto; margin-bottom: 15px;">${requestPayload}</pre>
          <h6 style="margin-bottom: 5px; font-weight: bold; font-size: 14px;">API Response:</h6>
          <pre style="font-size: 11px; background: #f4f4f4; padding: 10px; border-radius: 5px; max-height: 200px; overflow-y: auto;">${responseData}</pre>
        </div>
      `;

      if (res.data?.success) {
        toast.success(message);
        await fetchData();
        Swal.fire({
          title: "API Success",
          html: alertHtml,
          icon: "success",
          width: '700px'
        });
      } else {
        toast.error(message);
        Swal.fire({
          title: "API Failed",
          html: alertHtml,
          icon: "error",
          width: '700px'
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("Error retrying credit service");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [client]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "customer_id",
        header: "Customer ID",
        size: 100,
      },
      {
        accessorKey: "customer_name",
        header: "Customer Name",
        size: 150,
      },
      {
        accessorKey: "customer_email",
        header: "Email",
        size: 200,
      },
      {
        accessorKey: "provider",
        header: "Provider",
        size: 150,
        Cell: ({ cell }) => (
          <span className="text-uppercase">{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "failure_count",
        header: "Total Failures",
        size: 100,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 150,
        Cell: ({ cell }) => (
          <span className="badge badge-danger p-2" style={{ textTransform: "capitalize" }}>
            {cell.getValue().replace("_", " ")}
          </span>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Date Logged",
        size: 200,
        Cell: ({ cell }) => (
          <span>{moment(cell.getValue()).format("DD MMM YYYY, hh:mm A")}</span>
        ),
      },
      {
        id: "actions",
        header: "Action",
        size: 100,
        Cell: ({ row }) => (
          <button 
            className="btn btn-primary btn-sm"
            onClick={() => handleRetry(row.original.customer_id, row.original.provider)}
            disabled={isLoading}
          >
            Retry Now
          </button>
        )
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    state: { isLoading },
    enableSorting: true,
    enableColumnOrdering: true,
    enableGlobalFilter: true,
    initialState: {
      density: "compact",
    },
    muiTablePaperProps: {
      elevation: 0,
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: "transparent",
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: "transparent",
      },
    },
  });

  return (
    <Fragment>
      <Breadcrumbs
        mainTitle="Failed Credit Services"
        parent="Pages"
        title="Failed Credit Services"
      />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody className="p-0">
                <MaterialReactTable table={table} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default FailedCreditServices;
